import { useEffect, useState } from "react";
import { IoMdCloseCircle } from "react-icons/io";
import { LuCopy, LuCopyCheck } from "react-icons/lu";
import { getUserAccess } from "../../../clientDetails/services/userAccess.service.js";
import { getUserBindings } from "../../services/getUserBindings.js";
import SupportAccessModal from "../supportAccessModal/index.jsx";
import styles from "./css/userBindingsModal.module.css";

const PERMISSION_LABELS = [
  { key: "access_dashboard", label: "Dashboard" },
  { key: "ctrl_tables", label: "Mesas" },
  { key: "ctrl_categories", label: "Categorias" },
  { key: "ctrl_products", label: "Produtos" },
  { key: "ctrl_printers", label: "Impressoras" },
  { key: "ctrl_sell_channels", label: "Canais de venda" },
  { key: "ctrl_orders", label: "Pedidos" },
  { key: "reg_orders", label: "Reg. pedidos" },
  { key: "reg_commands", label: "Reg. comandas" },
  { key: "ctrl_commands", label: "Comandas" },
  { key: "ctrl_reserves", label: "Reservas" },
  { key: "ctrl_collaborators", label: "Colaboradores" },
  { key: "ctrl_clients", label: "Clientes" },
  { key: "ctrl_events", label: "Eventos" },
  { key: "access_logs", label: "Logs" },
  { key: "ctrl_sequences", label: "Sequencias" },
  { key: "ctrl_fiscal_emission", label: "Emissao fiscal" },
  { key: "ctrl_cash_book", label: "Livro caixa" },
  { key: "ctrl_stock", label: "Estoque" },
  { key: "ctrl_docs", label: "Documentos" },
];

function formatCargoLabel(cargo) {
  if (!cargo) return "Sem cargo";
  if (cargo.toLowerCase() === "owner") return "Dono";
  return cargo;
}

function formatDateTime(value) {
  if (!value) return "—";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return "—";
  return parsed.toLocaleString("pt-BR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export default function UserBindingsModal({ show, user, canViewAccess, canAccessAccount, onClose }) {
  const [bindings, setBindings] = useState([]);
  const [showSupportAccess, setShowSupportAccess] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [access, setAccess] = useState({ email: null, password: null });
  const [accessLoading, setAccessLoading] = useState(false);
  const [accessError, setAccessError] = useState(null);
  const [copyingEmail, setCopyingEmail] = useState(false);
  const [copyingPassword, setCopyingPassword] = useState(false);

  // FECHAR O MODAL DO USUÁRIO TAMBÉM FECHA O DE ACESSO DE SUPORTE (O ESTADO SOBREVIVE AO `return null`)
  useEffect(() => {
    if (!show) setShowSupportAccess(false);
  }, [show]);

  useEffect(() => {
    const handleGetBindings = async () => {
      try {
        if (!show || !user?.id) return;

        setLoading(true);
        setError(null);

        const data = await getUserBindings({ userId: user.id });
        setBindings(data);

      } catch (err) {
        console.error("ERROR - GET USER BINDINGS FUNCTION:", err);
        setBindings([]);
        setError("Nao foi possivel carregar os vinculos no momento.");

      } finally {
        setLoading(false);
      }
    };

    handleGetBindings();
  }, [show, user]);

  useEffect(() => {
    const handleGetAccess = async () => {
      try {
        if (!show || !user?.id || !canViewAccess) return;

        setAccessLoading(true);
        setAccessError(null);
        setAccess({ email: null, password: null });

        const data = await getUserAccess({ userId: user.id });
        setAccess(data);

      } catch (err) {
        console.error("ERROR - GET USER ACCESS FUNCTION:", err);
        setAccessError(err?.response?.data?.error || "Nao foi possivel buscar o acesso deste usuario no momento.");

      } finally {
        setAccessLoading(false);
      }
    };

    handleGetAccess();
  }, [show, user, canViewAccess]);

  if (!show) return null;

  const handleCopyEmail = () => {
    try {
      setCopyingEmail(true);
      navigator.clipboard.writeText(access.email);

      setTimeout(() => {
        setCopyingEmail(false);
      }, 5000);

    } catch (err) {
      console.error("Erro ao copiar o e-mail: ", err);
      setCopyingEmail(false);
    }
  };

  const handleCopyPassword = () => {
    try {
      setCopyingPassword(true);
      navigator.clipboard.writeText(access.password);

      setTimeout(() => {
        setCopyingPassword(false);
      }, 5000);

    } catch (err) {
      console.error("Erro ao copiar a senha: ", err);
      setCopyingPassword(false);
    }
  };

  return (
    <div className={styles.modalOverlay} onClick={onClose}>
      <div className={styles.modalContainer} onClick={(e) => e.stopPropagation()}>
        <header className={styles.modalHeader}>
          <h2 className={styles.modalTitle}>
            Detalhes de {user?.name || "usuário"}
          </h2>

          <button type="button" className={styles.closeButton} onClick={onClose}>
            <IoMdCloseCircle size={24} color="#2f3e49" />
          </button>
        </header>

        <div className={styles.modalBody}>
          {canAccessAccount && !user?.deleted && (
            <div className={styles.supportActionRow}>
              <button
                type="button"
                className={styles.supportButton}
                disabled={loading || bindings.length === 0}
                onClick={() => setShowSupportAccess(true)}
              >
                Acessar
              </button>
            </div>
          )}

          {canViewAccess && (
            <div className={styles.accessSection}>
              <p className={styles.sectionTitle}>Acesso</p>

              {accessLoading ? (
                <p className={styles.feedbackText}>Carregando acesso...</p>

              ) : accessError ? (
                <p className={styles.feedbackText}>{accessError}</p>

              ) : access.password ? (
                <div className={styles.credentialsArea}>
                  <div className={styles.infoCredentialContainer}>
                    <p><b>Email:</b></p>

                    <div className={styles.infoRowContainer}>
                      <div className={styles.infoContainer}>
                        <p>{access.email}</p>
                      </div>

                      <button
                        type="button"
                        className={styles.copyCredentialButton}
                        style={{ backgroundColor: copyingEmail ? "green" : "#2eb4f1" }}
                        onClick={handleCopyEmail}
                      >
                        {copyingEmail ? <LuCopyCheck size={18} color="#fff" /> : <LuCopy size={18} color="#fff" />}
                      </button>
                    </div>
                  </div>

                  <div className={styles.infoCredentialContainer}>
                    <p><b>Senha:</b></p>

                    <div className={styles.infoRowContainer}>
                      <div className={styles.infoContainer}>
                        <p>{access.password}</p>
                      </div>

                      <button
                        type="button"
                        className={styles.copyCredentialButton}
                        style={{ backgroundColor: copyingPassword ? "green" : "#2eb4f1" }}
                        onClick={handleCopyPassword}
                      >
                        {copyingPassword ? <LuCopyCheck size={18} color="#fff" /> : <LuCopy size={18} color="#fff" />}
                      </button>
                    </div>
                  </div>
                </div>

              ) : (
                <p className={styles.feedbackText}>Acesso indisponivel para este usuario.</p>
              )}
            </div>
          )}

          <p className={styles.sectionTitle}>Vínculos</p>

          {loading ? (
            <p>Carregando vínculos...</p>

          ) : error ? (
            <p>{error}</p>

          ) : bindings.length === 0 ? (
            <p>Este usuário não possui nenhum vínculo registrado.</p>

          ) : (
            <div className={styles.listArea}>
              {bindings.map((item) => (
                <article key={item.id} className={styles.bindingCard}>
                  <div className={styles.bindingCardHeader}>
                    <p className={styles.companyName}>{item.companyName}</p>
                    <span className={styles.cargoBadge}>{formatCargoLabel(item.cargo)}</span>
                  </div>

                  <p className={styles.linkedAtText}>
                    <b>Vinculado em:</b> {formatDateTime(item.linkedAt)}
                  </p>

                  {item.cargo?.toLowerCase() !== "owner" && (
                    <>
                      <p className={styles.permissionsTitle}>Permissões concedidas</p>
                      <div className={styles.permissionsGrid}>
                        {PERMISSION_LABELS.map(({ key, label }) => {
                          const granted = item.permissions?.[key];
                          return (
                            <span
                              key={key}
                              className={`${styles.permissionItem} ${granted ? styles.permissionOn : styles.permissionOff}`}
                            >
                              {granted ? "✓" : "✗"} {label}
                            </span>
                          );
                        })}
                      </div>
                    </>
                  )}
                </article>
              ))}
            </div>
          )}
        </div>
      </div>

      <SupportAccessModal
        show={showSupportAccess}
        user={user}
        bindings={bindings}
        onClose={() => setShowSupportAccess(false)}
      />
    </div>
  );
}
